import { t } from '@lingui/macro';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  searchIdentities,
  PeopleTaggingStatus,
  type AttachmentFaceResponse,
} from '@api/v4/private/people_tagging/faces';
import { usePeopleTaggingFaces } from '@components/asset/modal/tabs/shared/hooks/usePeopleTaggingFaces';
import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { Person, PeopleTaggingBackfillState } from '@components/people_tags/customPeopleTagTypes';
import { TextSanitizationTooltip } from '@components/shared/sanitization';

import assetModalContext from '../asset_modal_context';
import { ActionTypes, PeopleTaggingChangeType, type PeopleTaggingChange } from '../EditTabTypes';

import { PeopleTagEditField, type FaceData } from './PeopleTagEditField';
import { SameNameDetectedModal } from './SameNameDetectedModal';
import Wrapper, {
  PeopleSection,
  SectionHeader,
  HeaderText,
  PeopleContent,
  PeopleTagsContainer
} from './styles/PeopleInAssetsComponents';

interface PendingPersonData {
  name: string;
  faceId: string;
  identityId: string;
  nameSessionStartedAtMs?: number;
}

function PeopleInAsset(): React.ReactElement {
  const { state, dispatch } = useContext(assetModalContext);

  // Get attachment key and brandfolder key
  const attachmentKey = state?.editState?.firstAttachmentKey || '';
  const brandfolderKey = BFG?.brandfolder_key || '';

  const [isTabActive, setIsTabActive] = useState<boolean>(false);
  const nameSessionStartsRef = useRef<Record<string, number>>({});

  const touchNameSessionStart = useCallback((faceId: string) => {
    if (!nameSessionStartsRef.current[faceId]) {
      nameSessionStartsRef.current[faceId] = Date.now();
    }
  }, []);

  // Check if edit tab is active/visible
  useEffect(() => {
    const checkTabActive = (): void => {
      const editTabPane = document.getElementById('edit');
      const isActive = editTabPane?.classList.contains('active') ?? false;
      setIsTabActive(isActive);
    };

    // Check initially
    checkTabActive();

    const editTabPane = document.getElementById('edit');
    if (!editTabPane) return;

    // Use MutationObserver to watch for class changes on the tab pane
    const observer = new MutationObserver(() => {
      checkTabActive();
    });

    // Observe changes to the 'class' attribute
    observer.observe(editTabPane, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Also listen for Bootstrap tab show event (jQuery event)
    const handleTabShown = (): void => {
      setTimeout(checkTabActive, 0);
    };

    // Listen on the tab link element if it exists
    const editTabLink = document.querySelector('a[href="#edit"]');
    type JQueryElement = {
      on: (event: string, handler: () => void) => void;
      off: (event: string, handler: () => void) => void;
    };
    let jqueryElement: JQueryElement | null = null;

    if (editTabLink) {
      const jquery = (window as unknown as { $?: unknown }).$;
      if (jquery && typeof jquery === 'function') {
        jqueryElement = (jquery as (selector: Element) => JQueryElement)(editTabLink);
        jqueryElement.on('shown.bs.tab', handleTabShown);
      } else {
        editTabLink.addEventListener('click', handleTabShown);
      }
    }

    // Also listen on document as fallback
    document.addEventListener('shown.bs.tab', handleTabShown);

    return (): void => {
      observer.disconnect();
      if (editTabLink) {
        if (jqueryElement) {
          jqueryElement.off('shown.bs.tab', handleTabShown);
        } else {
          editTabLink.removeEventListener('click', handleTabShown);
        }
      }
      document.removeEventListener('shown.bs.tab', handleTabShown);
    };
  }, []);

  const backfillState: PeopleTaggingBackfillState =
    BFG.brandfolderSettings?.people_tagging_ui_state ?? PeopleTaggingBackfillState.Ready;
  const isBackfillReady = backfillState === PeopleTaggingBackfillState.Ready;

  // Only poll for faces when backfilling is complete
  const { faces: facesResponse, isLoading: isFacesLoading, status } = usePeopleTaggingFaces({
    attachmentKey,
    brandfolderKey,
    enabled: isTabActive && isBackfillReady
  });

  // Transform faces response to FaceData format
  const facesFromHook: FaceData[] = useMemo(() => {
    return facesResponse.map((face: AttachmentFaceResponse) => ({
      identityId: face.identity_id,
      faceId: face.id,
      name: face.identity_name || '',
      assetUrl: face.source_url,
      faceThumbnailUrl: face.face_thumbnail_url,
      isHidden: JSON.parse(face.identity_metadata || '{}')?.hide || false,
      faceCount: face.face_count || 0,
    }));
  }, [facesResponse]);

  // Local state for optimistic UI updates
  const [optimisticFaces, setOptimisticFaces] = useState<FaceData[]>(facesFromHook);

  // Sync local state with hook data (when hook updates, reset optimistic state)
  useEffect(() => {
    setOptimisticFaces(facesFromHook);
  }, [facesFromHook]);

  // Use optimistic faces for UI (allows for optimistic updates)
  const faces = optimisticFaces;

  // State for same name modal
  const [showSameNameModal, setShowSameNameModal] = useState(false);
  const [pendingPersonData, setPendingPersonData] = useState<PendingPersonData | null>(null);

  // Handle identifying a person from dropdown or creating new one
  const onMergeIdentities = useCallback((
    sourceIdentityId: string,
    destinationIdentity: Person,
    faceId: string,
  ): void => {
    if (!dispatch) return;

    const destinationIdentityId = destinationIdentity.person_id;

    // Update local UI state optimistically
    setOptimisticFaces((prev) =>
      prev.map((f) =>
        f.faceId === faceId ? { ...f, name: destinationIdentity.tag_name } : f
      )
    );

    // If source and destination are the same identity, the user reverted an unsaved change
    // (e.g., removed a name then re-selected the original before saving). Cancel the pending change.
    if (destinationIdentityId === sourceIdentityId) {
      const currentChanges = state?.editState?.peopleTaggingChanges || [];
      const filteredChanges = currentChanges.filter((change) => change.identityId !== sourceIdentityId);
      dispatch({
        type: ActionTypes.UpdateAsset,
        payload: { peopleTaggingChanges: filteredChanges }
      });
      return;
    }

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const newChange: PeopleTaggingChange = {
      destinationId: destinationIdentityId,
      destinationName: destinationIdentity.tag_name,
      faceId,
      mergeDestinationFaceCountBefore: destinationIdentity.face_count ?? destinationIdentity.tag_count ?? 0,
      sourceId: sourceIdentityId,
      sourceName: '',
      type: PeopleTaggingChangeType.MergeIdentity,
    };

    // Remove any existing change for this faceId and add new one
    const filteredChanges = currentChanges.filter((change) => change.faceId !== faceId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });
  }, [dispatch, state?.editState?.peopleTaggingChanges]);

  const handleUpdateIdentityName = useCallback((
    identityId: string,
    name: string,
    faceId: string,
    nameSessionStartedAtMs?: number
  ): void => {
    if (!dispatch) return;

    const trimmedName = name.trim();
    const existingFace = optimisticFaces.find((f) => f.faceId === faceId);
    const previousName = existingFace?.name ?? '';
    const faceCountForAnalytics = existingFace?.faceCount ?? 0;
    const assetKey = state?.initialData?.assetKey ?? '';
    const visibleFaces = optimisticFaces.filter((f) => !f.isHidden);
    const totalFacesOnAsset = visibleFaces.length;
    const facesNamedOnAsset = visibleFaces.filter((f) => {
      const n = f.faceId === faceId ? trimmedName : f.name;
      return (n || '').trim() !== '';
    }).length;

    // Update local UI state optimistically
    setOptimisticFaces((prev) =>
      prev.map((f) =>
        f.faceId === faceId ? { ...f, name: trimmedName } : f
      )
    );

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const newChange: PeopleTaggingChange = {
      faceId,
      identityId,
      name: trimmedName,
      type: PeopleTaggingChangeType.UpdateIdentity,
      previousName,
      faceCount: faceCountForAnalytics,
      assetKey,
      nameSessionStartedAtMs,
      totalFacesOnAsset,
      facesNamedOnAsset,
    };

    // Remove any existing change for this identityId and add new one
    const filteredChanges = currentChanges.filter((change) => change.faceId !== faceId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });

    delete nameSessionStartsRef.current[faceId];
    setShowSameNameModal(false);
    setPendingPersonData(null);
  }, [
    dispatch,
    optimisticFaces,
    state?.editState?.peopleTaggingChanges,
    state?.initialData?.assetKey,
    setShowSameNameModal,
  ]);

  // Handle creating a new person with the entered name
  const onUpdateIdentityName = useCallback(async (
    identityId: string,
    name: string,
    faceId: string,
    nameSessionStartedAtMs?: number
  ): Promise<void> => {
    const trimmedName = name.trim();
    const sessionStart = nameSessionStartedAtMs ?? nameSessionStartsRef.current[faceId];

    if (!trimmedName) {
      // Empty name, just update
      handleUpdateIdentityName(identityId, '', faceId);
      return;
    }

    try {
      // Check if there's an existing identity with the same name using search
      const existingIdentities = await searchIdentities({
        brandfolderKey,
        search: trimmedName,
        limit: 10
      });

      // Look for exact name match (case-insensitive)
      const existingIdentityHaveSameName = existingIdentities.find((identity) =>
        identity.name.toLowerCase() === trimmedName.toLowerCase()
      );

      if (existingIdentityHaveSameName) {
        // Show same name modal
        setPendingPersonData({
          name: trimmedName,
          faceId,
          identityId,
          nameSessionStartedAtMs: sessionStart,
        });
        setShowSameNameModal(true);
      } else {
        // Create a new person with the entered name
        handleUpdateIdentityName(identityId, trimmedName, faceId, sessionStart);
      }
    } catch (error) {
      console.error(t`Error checking for existing identity:`, error);
      handleUpdateIdentityName(identityId, trimmedName, faceId, sessionStart);
    }
  }, [brandfolderKey, handleUpdateIdentityName]);

  const onSplitIdentity = useCallback((
    identityId: string,
    faceId: string,
    name?: string,
    targetIdentityId?: string
  ): void => {
    if (!dispatch) return;

    setOptimisticFaces((prev) =>
      prev.map((f) =>
        f.faceId === faceId ? { ...f, name: name } : f
      )
    );

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const assetKey = state?.initialData?.assetKey ?? '';

    const newChange: PeopleTaggingChange = {
      type: PeopleTaggingChangeType.SplitIdentity,
      assetKey,
      identityId,
      faceId,
      name: targetIdentityId ? '' : name,  // If targetIdentityId is provided, name should be empty
      targetIdentityId,
    };

    // Remove any existing change for this faceId and add new one
    const filteredChanges = currentChanges.filter((change) => change.faceId !== faceId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });
  }, [dispatch, state?.editState?.peopleTaggingChanges, state?.initialData?.assetKey]);

  // Handle canceling the same name modal - just close it without taking any action
  const onCancelSameNameModal = useCallback((): void => {
    // Just close the modal and reset state
    setShowSameNameModal(false);
    setPendingPersonData(null);
  }, []);

  // Hide the entire people section while backfilling is in progress
  if (!isBackfillReady) {
    return null;
  }

  const showLoader = isFacesLoading || status === PeopleTaggingStatus.WaitingForPeopleTagging;

  if (showLoader) {
    return (
      <I18nProviderWrapper>
        <Wrapper>
          <PeopleSection>
            <SectionHeader>
              <HeaderText>{t`People in asset`}</HeaderText>
            </SectionHeader>
            <PeopleContent>
              <div style={{ padding: '20px', textAlign: 'center', width: '100%' }}>
                <span className="bff-spinner-gif" />
              </div>
            </PeopleContent>
          </PeopleSection>
        </Wrapper>
      </I18nProviderWrapper>
    );
  }

  const onlyVisibleFaces = faces.filter((face) => !face.isHidden);

  if (onlyVisibleFaces.length === 0) {
    return null;
  }

  return (
    <I18nProviderWrapper>
      <Wrapper>
        <PeopleSection>
          <SectionHeader>
            <HeaderText>{t`People in asset`}</HeaderText>
            <TextSanitizationTooltip
              tooltipText={t`
                Name the faces in this asset by selecting from existing people or typing a new name.
                If a face was misidentified, remove the name and enter the correct one.
              `}
            />
          </SectionHeader>
          <PeopleContent>
            <PeopleTagsContainer>
              {faces.map((face) => {
                if (face.isHidden) {
                  return null;
                }

                return (
                <PeopleTagEditField
                  key={face.identityId}
                  assetCount={face.faceCount}
                  face={face}
                  onMergeIdentities={onMergeIdentities}
                  onSplitIdentity={onSplitIdentity}
                  onUnnamedNameTypingStart={touchNameSessionStart}
                  onUpdateIdentityName={(identityId, name, faceId): void => {
                    void onUpdateIdentityName(
                      identityId,
                      name,
                      faceId,
                      nameSessionStartsRef.current[faceId]
                    );
                  }}
                />
                );
              })}
          </PeopleTagsContainer>
          </PeopleContent>
        </PeopleSection>
      </Wrapper>
      {/* Same Name Detected Modal */}
      <SameNameDetectedModal
        isOpen={showSameNameModal}
        onCancel={onCancelSameNameModal}
        onCreateNew={(): void => {
          if (
            pendingPersonData?.identityId &&
            pendingPersonData.faceId != null
          ) {
            handleUpdateIdentityName(
              pendingPersonData.identityId,
              pendingPersonData.name,
              pendingPersonData.faceId,
              pendingPersonData.nameSessionStartedAtMs
            );
          }
        }}
      />
    </I18nProviderWrapper>
  );
}

export default PeopleInAsset;
